import { useEffect, useRef, useState, useCallback } from "react";
import { Megaphone, Send, Upload, X } from "lucide-react";
import type { BroadcastAudienceRecipientDto } from "@gatiq/shared";
import {
  Page,
  PageBody,
  PageHeader,
  Card,
  CardHeader,
  Badge,
  Alert,
  Button,
  TextArea,
  Pagination,
  EmptyState,
  Table,
  THead,
  TH,
  TR,
  TD,
  TDStacked,
} from "@/ui";
import { useCan } from "@/auth/access";
import { api } from "@/lib/api";

type Audience = string;
type LocationKey = "city" | "area" | "district" | "state" | "pincode";
type LocationFilterKey = "district" | "state" | "pincode";
type Target = Record<LocationKey, string[]> & {
  counterIds: string[];
  karigarIds: string[];
  recipientMode: "all" | "selected";
  recipientIds: string[];
};
type GeoRecord = Record<LocationKey, string> & { roleKey: string };
type RecipientOptions = {
  roles: Array<{ value: string; label: string; base: string; level: number }>;
  geoRecords: GeoRecord[];
};
const EMPTY_TARGET: Target = { city: [], area: [], district: [], state: [], pincode: [], counterIds: [], karigarIds: [], recipientMode: "all", recipientIds: [] };
const EMPTY_RECIPIENT_OPTIONS: RecipientOptions = { roles: [], geoRecords: [] };
type HistoricalTarget = Partial<Record<LocationKey, string | string[]>> & Partial<Pick<Target, "counterIds" | "karigarIds" | "recipientMode" | "recipientIds">>;
type AudiencePreview = { total: number; matchingTotal: number; items: BroadcastAudienceRecipientDto[]; page: number; pageSize: number; totalPages: number };
const LOCATION_ORDER: LocationFilterKey[] = ["state", "district", "pincode"];
const LOCATION_LABELS: Record<LocationFilterKey, string> = {
  state: "State",
  district: "District",
  pincode: "PIN code",
};

function MultiCheckboxDropdown({
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const dropdownRef = useRef<HTMLDetailsElement>(null);
  const matching = options.filter((option) => option.label.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const selectedLabels = options.filter((option) => value.includes(option.value)).map((option) => option.label);
  const selectedText = selectedLabels.length > 2
    ? selectedLabels.slice(0, 2).join(", ") + " +" + (selectedLabels.length - 2)
    : selectedLabels.join(", ");

  useEffect(() => {
    const dropdown = dropdownRef.current;
    if (!dropdown) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !dropdown.contains(event.target)) dropdown.open = false;
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !dropdown.open) return;
      dropdown.open = false;
      dropdown.querySelector("summary")?.focus();
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <details
      className="group relative min-w-0"
      data-audience-filter-dropdown
      ref={dropdownRef}
      onToggle={(event) => {
        const current = event.currentTarget;
        if (!current.open) {
          setQuery("");
          return;
        }
        document.querySelectorAll<HTMLDetailsElement>("details[data-audience-filter-dropdown]").forEach((details) => {
          if (details !== current) details.open = false;
        });
      }}
    >
      <summary className={"flex h-10 cursor-pointer list-none items-center justify-between gap-2 rounded-md border border-rule bg-surface px-3 text-sm text-ink shadow-sm " + (disabled ? "pointer-events-none opacity-60" : "hover:border-brand-border")}>
        <span className="truncate">{value.length ? label + ": " + selectedText : placeholder}</span>
        <span aria-hidden="true" className="text-muted transition-transform group-open:rotate-180">⌄</span>
      </summary>
      <div className="absolute z-30 mt-1 w-full min-w-64 rounded-lg border border-rule bg-raised p-2 shadow-xl">
        <input
          aria-label={"Search " + label}
          className="mb-2 h-9 w-full rounded-md border border-rule bg-surface px-2.5 text-sm text-ink"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={"Search " + label.toLocaleLowerCase() + "…"}
          type="search"
          value={query}
        />
        <div className="max-h-56 overflow-y-auto">
          {matching.length ? matching.map((option) => (
            <label key={option.value} className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm text-ink hover:bg-brand-light">
              <input
                checked={value.includes(option.value)}
                className="mt-0.5 accent-brand"
                onChange={() => onChange(value.includes(option.value) ? value.filter((item) => item !== option.value) : [...value, option.value])}
                type="checkbox"
                value={option.value}
              />
              <span className="min-w-0 break-words">{option.label}</span>
            </label>
          )) : <p className="px-2 py-3 text-sm text-muted">No matching options.</p>}
        </div>
        <div className="mt-2 flex items-center justify-between border-t border-rule pt-2">
          <span className="text-xs text-muted">{options.length.toLocaleString("en-IN")} available</span>
          <button className="text-xs font-medium text-brand-ink hover:underline" onClick={() => onChange([])} type="button">Clear selection</button>
        </div>
      </div>
    </details>
  );
}

function targetValues(value: string | string[] | undefined): string[] {
  return Array.isArray(value) ? value : value ? [value] : [];
}

function targetSummary(target: HistoricalTarget): string {
  const city = targetValues(target.city);
  const area = targetValues(target.area);
  const district = targetValues(target.district);
  const state = targetValues(target.state);
  const pincode = targetValues(target.pincode);
  return [
    city.length ? "City: " + city.join(", ") : "",
    area.length ? "Area: " + area.join(", ") : "",
    district.length ? "District: " + district.join(", ") : "",
    state.length ? "State: " + state.join(", ") : "",
    pincode.length ? "PIN: " + pincode.join(", ") : "",
    target.counterIds?.length ? target.counterIds.length + " counter(s)" : "",
    target.karigarIds?.length ? target.karigarIds.length + " individual karigar(s)" : "",
    target.recipientMode === "selected" ? (target.recipientIds?.length ?? 0) + " selected recipient(s)" : "",
  ].filter(Boolean).join(" · ");
}

type Progress = { total: number; sent: number; failed: number; done: boolean };
type DrainResult = { active: boolean; total?: number; sent?: number; failed?: number; done?: boolean };

type BroadcastItem = {
  id: string;
  audiences: Audience[];
  target?: HistoricalTarget;
  message: string;
  imageUrl?: string;
  status: string;
  total: number;
  sent: number;
  failed: number;
  createdByName: string;
  createdAt: string;
};

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

function imageDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the selected image."));
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Could not read the selected image."));
    reader.readAsDataURL(file);
  });
}

export function BroadcastsPage() {
  const can = useCan();
  const canView = can("broadcasts", "view");
  const canCreate = can("broadcasts", "create");

  const [broadcasts, setBroadcasts] = useState<BroadcastItem[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [audiencePreview, setAudiencePreview] = useState<AudiencePreview | null>(null);
  const [audiences, setAudiences] = useState<Audience[]>(["all"]);
  const [target, setTarget] = useState<Target>(EMPTY_TARGET);
  const [recipientOptions, setRecipientOptions] = useState<RecipientOptions>(EMPTY_RECIPIENT_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [message, setMessage] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<null | "test" | "send">(null);
  const [feedback, setFeedback] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [page, setPage] = useState(1);
  const [recipientPageState, setRecipientPageState] = useState({ key: "", page: 1 });
  const [loaded, setLoaded] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadBroadcasts = useCallback(async () => {
    setHistoryError(null);
    try {
      const data = await api.get<BroadcastItem[]>("/org/broadcasts");
      setBroadcasts(data);
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : "Could not load broadcast history.");
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (canView) void loadBroadcasts();
  }, [canView, loadBroadcasts]);

  useEffect(() => {
    if (!canView) return;
    api.get<RecipientOptions>("/org/broadcasts/recipient-options")
      .then(setRecipientOptions)
      .catch(() => setFeedback({ type: "error", text: "Could not load configured roles and location data." }))
      .finally(() => setOptionsLoading(false));
  }, [canView]);

  const audienceFilterKey = JSON.stringify({
    audiences,
    city: target.city,
    area: target.area,
    district: target.district,
    state: target.state,
    pincode: target.pincode,
  });
  const recipientPage = recipientPageState.key === audienceFilterKey ? recipientPageState.page : 1;

  useEffect(() => {
    if (optionsLoading) {
      setPreviewLoading(true);
      return;
    }
    if (audiences.length === 0) {
      setCount(null);
      setAudiencePreview(null);
      setPreviewLoading(false);
      setPreviewError(null);
      return;
    }
    setCount(null);
    setAudiencePreview(null);
    setPreviewLoading(true);
    setPreviewError(null);
    let cancelled = false;
    let requestTimeout: number | undefined;
    const timer = window.setTimeout(() => {
      requestTimeout = window.setTimeout(() => {
        if (cancelled) return;
        cancelled = true;
        setPreviewLoading(false);
        setPreviewError("The recipient request took too long. Check your connection and retry.");
      }, 20_000);
      api
        .post<AudiencePreview>("/org/broadcasts/audience-preview", { audiences, target, page: recipientPage, pageSize: 20 })
        .then((r) => {
          if (cancelled) return;
          setCount(r.total);
          setAudiencePreview(r);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setCount(null);
          setPreviewError(error instanceof Error ? error.message : "Could not load recipients.");
        })
        .finally(() => {
          window.clearTimeout(requestTimeout);
          if (!cancelled) setPreviewLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (requestTimeout !== undefined) window.clearTimeout(requestTimeout);
    };
  }, [audiences, target, recipientPage, previewAttempt, optionsLoading]);

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setFeedback({ type: "error", text: "Image must be PNG, JPEG, WebP, or GIF." });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setFeedback({ type: "error", text: "Image must be under 5 MB." });
      return;
    }
    setFeedback(null);
    setImage(file);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }

  function removeImage() {
    setImage(null);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
    if (fileRef.current) fileRef.current.value = "";
  }

  function toggleRole(v: Audience) {
    if (v === "all") {
      setAudiences(["all"]);
      setTarget((old) => ({ ...old, recipientIds: [] }));
      return;
    }
    setAudiences((current) => {
      const selected = current.filter((role) => role !== "all");
      const next = selected.includes(v) ? selected.filter((role) => role !== v) : [...selected, v];
      return next.length ? next : ["all"];
    });
    setTarget((old) => ({ ...old, recipientIds: [] }));
  }

  async function onSend() {
    setBusy("send");
    setFeedback(null);
    setProgress(null);

    try {
      const imageData = image ? await imageDataUrl(image) : undefined;
      const data = await api.post<{ id: string; total: number }>("/org/broadcasts", {
        audiences,
        target,
        message,
        ...(imageData ? { imageDataUrl: imageData } : {}),
      });

      setProgress({ total: data.total ?? 0, sent: 0, failed: 0, done: false });
      setFeedback({
        type: "success",
        text: `Broadcast started to ${data.total} recipient(s). Keep this page open to send faster.`,
      });
      setMessage("");
      setAudiences(["all"]);
      setTarget(EMPTY_TARGET);
      setCount(null);
      removeImage();
      try {
        await pollDrain();
      } catch (error) {
        const detail = error instanceof Error ? " " + error.message : "";
        setFeedback({
          type: "error",
          text: "The broadcast was queued, but its sending progress could not be refreshed." + detail + " Check Recent broadcasts before trying again.",
        });
      }
      await loadBroadcasts();
    } catch (e) {
      setFeedback({ type: "error", text: e instanceof Error ? e.message : "Failed to start broadcast." });
    } finally {
      setBusy(null);
    }
  }

  async function pollDrain() {
    for (;;) {
      const data = await api.post<DrainResult>("/org/broadcasts/drain");
      if (!data.active) {
        setProgress((current) => current ? { ...current, done: true } : current);
        break;
      }
      setProgress({ total: data.total ?? 0, sent: data.sent ?? 0, failed: data.failed ?? 0, done: !!data.done });
      if (data.done) break;
      await new Promise((r) => setTimeout(r, 1500));
    }
  }

  const audienceChoices = [
    { value: "all", label: "All roles" },
    ...recipientOptions.roles,
  ];
  const selectedAudienceKeys = audiences.includes("all")
    ? recipientOptions.roles.map((role) => role.value)
    : audiences;
  const canSend = audiences.length > 0 && message.trim().length > 0 && busy === null && count !== null && count > 0 && !optionsLoading;

  const activeRoleKeys = new Set(selectedAudienceKeys);
  const locationOptions = (field: LocationFilterKey) => {
    const values = new Set<string>();
    recipientOptions.geoRecords.forEach((record) => {
      if (!activeRoleKeys.has(record.roleKey)) return;
      const compatible = LOCATION_ORDER.every((filterKey) => {
        if (filterKey === field) return true;
        const selected = target[filterKey];
        return selected.length === 0 || selected.includes(record[filterKey]);
      });
      if (compatible && record[field]) values.add(record[field]);
    });
    return [...values]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
      .map((value) => ({ value, label: value }));
  };

  function updateLocation(field: LocationFilterKey, values: string[]) {
    const fieldIndex = LOCATION_ORDER.indexOf(field);
    setTarget((current) => {
      const next: Target = { ...current, [field]: values, recipientIds: [] };
      LOCATION_ORDER.slice(fieldIndex + 1).forEach((dependent) => { next[dependent] = []; });
      return next;
    });
  }

  function toggleRecipient(id: string) {
    if (!target.recipientIds.includes(id) && target.recipientIds.length >= 500) {
      setFeedback({ type: "error", text: "You can select up to 500 individual recipients at a time." });
      return;
    }
    setFeedback(null);
    setTarget((current) => {
      const selected = new Set(current.recipientIds);
      if (selected.has(id)) selected.delete(id);
      else selected.add(id);
      return { ...current, recipientMode: "selected", recipientIds: [...selected] };
    });
  }

  const STATUS_TONE: Record<string, "neutral" | "warning" | "success"> = {
    queued: "neutral",
    sending: "warning",
    completed: "success",
  };

  if (!canView) {
    return (
      <Page fullWidth>
        <PageHeader title="Promotions" />
        <PageBody>
          <EmptyState title="Not authorized" message="You don't have access to this module." />
        </PageBody>
      </Page>
    );
  }

  const pageSize = 10;
  const totalPages = Math.ceil(broadcasts.length / pageSize);
  const pageItems = broadcasts.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Page fullWidth>
      <PageHeader title="Promotions" description="Choose recipients across your configured roles, then narrow by location or select individual people." />

      <PageBody className="flex flex-col gap-6">
        {canCreate && (
          <Card>
            <CardHeader title="New broadcast" />

            <div className="space-y-5 p-5">
              {/* Audiences */}
              <div>
                <label className="text-sm font-medium text-ink">Send to <span className="text-red-500">*</span></label>
                <p className="mt-1 text-xs text-muted">All roles is selected by default. Choose individual configured roles to narrow the recipients.</p>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {audienceChoices.map((a) => {
                    const active = audiences.includes(a.value);
                    return (
                      <button
                        key={a.value}
                        type="button"
                        onClick={() => toggleRole(a.value)}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                          active
                            ? "border-brand bg-brand text-white"
                            : "border-rule bg-raised text-muted hover:border-brand-border hover:text-brand-ink"
                        }`}
                      >
                        {a.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-muted">
                  {target.recipientMode === "selected"
                    ? `${count ?? "…"} selected recipient(s) will receive this message.`
                    : audiences.includes("all")
                    ? `${count ?? "…"} active recipients with a phone number across all configured roles.`
                    : `${count ?? "…"} active recipient(s) with a phone number in the selected roles.`}
                </p>
              </div>

              <div className="rounded-lg border border-rule bg-raised p-4">
                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  {LOCATION_ORDER.map((key) => {
                    const label = LOCATION_LABELS[key];
                    return (
                      <label key={key} className="min-w-0 text-xs font-medium text-ink">
                        {label}
                        <div className="mt-1">
                          <MultiCheckboxDropdown
                            label={label}
                            options={locationOptions(key)}
                            onChange={(values) => updateLocation(key, values)}
                            placeholder={"All " + label.toLocaleLowerCase()}
                            value={target[key]}
                            disabled={optionsLoading}
                          />
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setTarget((old) => ({ ...old, city: [], area: [], district: [], state: [], pincode: [], counterIds: [], karigarIds: [], recipientIds: [] }))}>Clear location filters</Button>
                  <span className="text-xs text-muted" aria-live="polite">{count === null ? "Calculating recipients..." : `${count.toLocaleString("en-IN")} recipient(s) will receive this broadcast.`}</span>
                </div>

                <section className="mt-5 overflow-hidden rounded-lg border border-rule bg-surface" aria-label="Matched recipients">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-4 py-3">
                    <div>
                      <h4 className="text-sm font-semibold text-ink">Recipients</h4>
                      <p className="mt-0.5 text-xs text-muted">One list for every selected role. Review each matched person and their linked counter.</p>
                    </div>
                    <div className="flex flex-wrap gap-2" role="group" aria-label="Recipient selection mode">
                      <button
                        type="button"
                        onClick={() => setTarget((old) => ({ ...old, recipientMode: "all", recipientIds: [] }))}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium ${target.recipientMode === "all" ? "border-brand bg-brand text-white" : "border-rule bg-raised text-muted hover:border-brand-border"}`}
                      >
                        All matching
                      </button>
                      <button
                        type="button"
                        onClick={() => setTarget((old) => ({ ...old, recipientMode: "selected" }))}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium ${target.recipientMode === "selected" ? "border-brand bg-brand text-white" : "border-rule bg-raised text-muted hover:border-brand-border"}`}
                      >
                        Choose people ({target.recipientIds.length})
                      </button>
                    </div>
                  </div>
                  {target.recipientMode === "selected" && <p className="border-b border-rule bg-brand-light/40 px-4 py-2 text-xs text-brand-ink">Choose up to 500 people from the list. The send count reflects only checked people.</p>}
                  {previewLoading ? (
                    <div className="p-6 text-center text-sm text-muted" role="status">Loading matching recipients...</div>
                  ) : previewError ? (
                    <div className="flex flex-col items-center gap-3 p-6 text-center">
                      <p className="text-sm text-red-600" role="alert">Could not load the recipient list: {previewError}</p>
                      <Button size="sm" variant="secondary" onClick={() => setPreviewAttempt((attempt) => attempt + 1)}>Retry recipient list</Button>
                    </div>
                  ) : !audiencePreview?.items.length ? (
                    <div className="p-6 text-center text-sm text-muted">No active recipients match the selected roles and locations.</div>
                  ) : (
                    <>
                      <Table searchable={false} filterable={false} scrollable>
                        <THead><tr>
                          {target.recipientMode === "selected" && <TH width="3.5rem"><span className="sr-only">Select</span></TH>}
                          <TH>Recipient</TH><TH>Role</TH><TH>Location</TH><TH>Linked counter</TH>
                        </tr></THead>
                        <tbody>
                          {audiencePreview.items.map((recipient) => {
                            const locationMain = [...new Set([recipient.city, recipient.area].filter(Boolean))].join(", ");
                            const locationDetail = [recipient.district, recipient.state, recipient.pincode].filter(Boolean).join(" · ");
                            return (
                              <TR key={recipient.id}>
                                {target.recipientMode === "selected" && <TD className="w-12 text-center"><input aria-label={`Select ${recipient.name}`} checked={target.recipientIds.includes(recipient.id)} className="size-4 accent-brand" onChange={() => toggleRecipient(recipient.id)} type="checkbox" /></TD>}
                                <TDStacked primary={recipient.name || "Unnamed recipient"} secondary={recipient.phone || "No phone number"} />
                                <TD>{recipient.roleLabel}</TD>
                                <TD><div className="max-w-64"><p className="truncate">{locationMain || "Location not provided"}</p>{locationDetail && <p className="truncate text-xs text-muted">{locationDetail}</p>}</div></TD>
                                <TD>{recipient.counterName || "—"}</TD>
                              </TR>
                            );
                          })}
                        </tbody>
                      </Table>
                      {audiencePreview.totalPages > 1 && <Pagination page={recipientPage} totalPages={audiencePreview.totalPages} total={audiencePreview.matchingTotal} onChange={(nextPage) => setRecipientPageState({ key: audienceFilterKey, page: nextPage })} />}
                    </>
                  )}
                </section>
              </div>

              {/* Message */}
              <div>
                <label className="text-sm font-medium text-ink">Message <span className="text-red-500">*</span></label>
                <TextArea
                  label="Message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder="Write your promotional message…"
                  className="mt-1"
                />
              </div>

              {/* Image */}
              <div>
                <label className="text-sm font-medium text-ink">Image (optional)</label>
                {imagePreview ? (
                  <div className="relative mt-1 inline-block overflow-hidden rounded-lg border border-rule bg-raised">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={imagePreview} alt="Attachment preview" className="max-h-48 object-contain" />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
                      title="Remove image"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                ) : (
                  <div className="mt-1 flex items-center gap-3">
                    <input ref={fileRef} type="file" accept={ALLOWED_TYPES.join(",")} className="sr-only" id="promo-image" onChange={handleImageChange} />
                    <label
                      htmlFor="promo-image"
                      className="cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-rule bg-raised px-3 py-1.5 text-sm font-medium text-ink shadow-card transition-colors hover:bg-panel"
                    >
                      <Upload className="size-4" />
                      Attach image
                    </label>
                    <span className="text-xs text-muted">PNG · JPEG · WebP · GIF · max 5 MB</span>
                  </div>
                )}
                <p className="mt-1 text-xs text-muted">When attached, the message is sent as the image caption.</p>
              </div>

              {feedback && <Alert kind={feedback.type === "error" ? "danger" : "success"}>{feedback.text}</Alert>}

              {/* Progress */}
              {progress && (
                <div className="rounded-lg border border-rule bg-raised p-3">
                  <div className="flex items-center justify-between text-xs text-muted">
                    <span className="font-medium text-ink">{progress.done ? "Completed" : "Sending…"}</span>
                    <span>
                      {progress.sent}/{progress.total} sent{progress.failed > 0 ? ` · ${progress.failed} failed` : ""}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink/[0.07]">
                    <div
                      className={`h-full rounded-full transition-[width] duration-300 ${progress.done ? "bg-green-500" : "bg-brand"}`}
                      style={{ width: `${progress.total > 0 ? Math.round(((progress.sent + progress.failed) / progress.total) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={onSend} loading={busy === "send"} disabled={!canSend}>
                  <Megaphone className="size-4" />
                  {busy === "send" ? "Sending…" : "Send broadcast"}
                </Button>
              </div>

              <p className="text-xs text-amber-600">
                Sending bulk promotions from one WhatsApp number can get it flagged or banned. Messages are sent in small paced
                batches to reduce risk. Large audiences take time to finish.
              </p>
            </div>
          </Card>
        )}

        {/* History */}
        <div>
          <h2 className="mb-3 text-sm font-semibold text-ink">Recent broadcasts</h2>
          {!loaded ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-20 animate-pulse rounded-lg border border-rule bg-raised" />
              ))}
            </div>
          ) : historyError ? (
            <Card>
              <div className="space-y-3 p-4">
                <Alert kind="danger">Could not load broadcast history: {historyError}</Alert>
                <Button size="sm" variant="secondary" onClick={() => { setLoaded(false); void loadBroadcasts(); }}>Retry history</Button>
              </div>
            </Card>
          ) : broadcasts.length === 0 ? (
            <Card>
              <EmptyState message="Your broadcast history will show here after you send your first message." title="No broadcasts yet" />
            </Card>
          ) : (
            <>
              <div className="space-y-2">
                {pageItems.map((b) => {
                  const pct = b.total > 0 ? Math.round(((b.sent + b.failed) / b.total) * 100) : 0;
                  return (
                    <div key={b.id} className="rounded-lg border border-rule bg-panel p-4 shadow-card">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge tone={STATUS_TONE[b.status] ?? "neutral"}>{b.status}</Badge>
                            {b.audiences.map((r) => (
                              <span key={r} className="rounded-full bg-raised px-2 py-0.5 text-xs text-muted">
                                {r === "all"
                                  ? "All roles"
                                  : recipientOptions.roles.find((role) => role.value === r || (!r.includes(":") && role.base === r))?.label
                                    ?? (r === "karigar" ? "Karigars" : r.split(":")[0]?.replaceAll("_", " ") ?? r)}
                              </span>
                            ))}
                          </div>
                          {b.target && targetSummary(b.target) ? (
                            <p className="mt-1 text-xs text-muted">{targetSummary(b.target)}</p>
                          ) : null}
                          <p className="mt-1.5 line-clamp-2 whitespace-pre-wrap text-sm text-panel-text">{b.message}</p>
                        </div>
                        <div className="shrink-0 text-right text-xs text-muted">
                          <p className="font-medium text-ink">
                            {b.sent}/{b.total} sent
                          </p>
                          {b.failed > 0 && <p className="text-red-500">{b.failed} failed</p>}
                          <p className="mt-0.5">{new Date(b.createdAt).toLocaleDateString("en-IN")}</p>
                        </div>
                      </div>
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-ink/[0.07]">
                        <div
                          className={`h-full rounded-full ${b.status === "completed" ? "bg-green-500" : "bg-brand"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="mt-4">
                  <Pagination page={page} totalPages={totalPages} total={broadcasts.length} onChange={setPage} />
                </div>
              )}
            </>
          )}
        </div>
      </PageBody>
    </Page>
  );
}
